import React, { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Select,
  App,
  Alert,
  Checkbox,
  Segmented,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useGetOrdenesSinFacturarQuery,
  useGetClientesFiscalesQuery,
  useGetFacturasQuery,
  useGetFacturacionConfigQuery,
  useFacturarOrdenesMasivoMutation,
  useTimbrarFacturasMasivoMutation,
  useRegenerarFacturasMasivoMutation,
} from '../../services/facturacionApi';
import { useGetStampBalanceQuery } from '../../services/stripeApi';
import {
  OrdenSinFacturar,
  OrdenesSinFacturarFilter,
  FacturaListItem,
  FacturacionError,
  TimbradoResumen,
  RegeneracionResumen,
  ModoFacturacionMasiva,
  PeriodicidadGlobal,
} from '../../types/facturacion';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { getErrorMessage } from '../../utils/errorHelpers';
import MobileDateRangePicker from '../common/MobileDateRangePickerV2';
import { exportTablaCSV } from '../../utils/reportExport';

const { Option } = Select;
const { Text } = Typography;

// Helper function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
};

/** c_Periodicidad del SAT. La bimestral solo existe para el Régimen de Incorporación Fiscal (621). */
const PERIODICIDADES: { value: PeriodicidadGlobal; label: string }[] = [
  { value: '01', label: 'Diaria' },
  { value: '02', label: 'Semanal' },
  { value: '03', label: 'Quincenal' },
  { value: '04', label: 'Mensual' },
  { value: '05', label: 'Bimestral (solo RIF 621)' },
];

/** Filtros de forma de pago. "Tarjeta" junta crédito y débito (así hace su global quien factura solo tarjeta). */
const FORMAS_PAGO: { value: string; label: string }[] = [
  { value: 'tarjeta', label: 'Tarjeta (crédito y débito)' },
  { value: 'credit_card', label: 'Tarjeta de crédito' },
  { value: 'debit_card', label: 'Tarjeta de débito' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'transfer', label: 'Transferencia' },
];

const etiquetaFormaPago = (value: string) => FORMAS_PAGO.find((f) => f.value === value)?.label ?? value;

const FacturacionMasivaTab: React.FC = () => {
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { message, modal } = App.useApp();

  // Filter states
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [formaPagoFilter, setFormaPagoFilter] = useState<string | undefined>(undefined);
  const [selectedClienteId, setSelectedClienteId] = useState<number | undefined>(undefined);

  // Selection states
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [selectedFacturaIds, setSelectedFacturaIds] = useState<number[]>([]);

  // Global invoice mode states
  const [modoFacturacion, setModoFacturacion] = useState<ModoFacturacionMasiva>('individual');
  const [periodicidad, setPeriodicidad] = useState<PeriodicidadGlobal>('04');
  // La global ampara TODAS las ventas al público en general del periodo: por default se
  // toman en el backend, no solo las que caben en la tabla.
  const [todasLasDelPeriodo, setTodasLasDelPeriodo] = useState(true);
  const esGlobal = modoFacturacion === 'global';

  // Días locales del restaurante (yyyy-MM-dd); el backend los convierte con su zona horaria.
  const fechaInicioStr = dateRange[0]?.format('YYYY-MM-DD');
  const fechaFinStr = dateRange[1]?.format('YYYY-MM-DD');

  const ordeneFilter: OrdenesSinFacturarFilter = useMemo(() => {
    const filter = {
      fechaInicio: fechaInicioStr,
      fechaFin: fechaFinStr,
      formaPago: formaPagoFilter,
    };
    return filter;
  }, [fechaInicioStr, fechaFinStr, formaPagoFilter]);

  // API hooks
  const { data: ordenesResult, isLoading: loadingOrdenes, refetch: refetchOrdenes } = useGetOrdenesSinFacturarQuery(ordeneFilter);
  const ordenes = ordenesResult?.ordenes;
  const { data: clientes, isLoading: loadingClientes } = useGetClientesFiscalesQuery();
  const { data: facturacionConfig } = useGetFacturacionConfigQuery();
  const { data: stampBalance } = useGetStampBalanceQuery();
  const { data: facturasData, isLoading: loadingFacturas, refetch: refetchFacturas } = useGetFacturasQuery({
    estado: 'Borrador',
    pageNumber: 1,
    pageSize: 100,
  });

  const [facturarMasivo, { isLoading: facturando }] = useFacturarOrdenesMasivoMutation();
  const [timbrarMasivo, { isLoading: timbrando }] = useTimbrarFacturasMasivoMutation();
  const [regenerarMasivo, { isLoading: regenerando }] = useRegenerarFacturasMasivoMutation();

  const esRif = facturacionConfig?.regimenFiscal?.trim() === '621';
  const periodoCompleto = !!fechaInicioStr && !!fechaFinStr;
  const usarTodas = esGlobal && todasLasDelPeriodo;

  // Handle date range change
  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setDateRange(dates || [null, null]);
  };

  // Handle order selection
  const handleOrderSelectionChange = (selectedRowKeys: React.Key[]) => {
    setSelectedOrderIds(selectedRowKeys as string[]);
  };

  // Handle factura selection
  const handleFacturaSelectionChange = (selectedRowKeys: React.Key[]) => {
    setSelectedFacturaIds(selectedRowKeys as number[]);
  };

  const mostrarErrores = (titulo: string, errores: FacturacionError[]) => {
    modal.warning({
      title: titulo,
      content: (
        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          {errores.map((err: FacturacionError, idx: number) => (
            <Alert
              key={idx}
              type="error"
              message={err.orderNumber ? `Orden ${err.orderNumber}` : 'Aviso'}
              description={err.error}
              style={{ marginBottom: 8 }}
            />
          ))}
        </div>
      ),
    });
  };

  // Handle bulk invoice generation
  const handleFacturarMasivo = async () => {
    if (esGlobal) {
      if (!periodoCompleto) {
        message.warning('Elige el periodo (inicio y fin) de la factura global');
        return;
      }
      if (!usarTodas && selectedOrderIds.length === 0) {
        message.warning('Selecciona las órdenes o marca "todas las del periodo"');
        return;
      }
    } else {
      if (selectedOrderIds.length === 0) {
        message.warning('Selecciona al menos una orden para facturar');
        return;
      }
      if (!selectedClienteId) {
        message.warning('Selecciona un cliente fiscal');
        return;
      }
    }

    const periodicidadLabel = PERIODICIDADES.find((p) => p.value === periodicidad)?.label ?? periodicidad;
    const cuantas = usarTodas ? `todas las órdenes sin facturar del periodo` : `${selectedOrderIds.length} órdenes seleccionadas`;
    const confirmTitle = esGlobal ? 'Confirmar factura global' : 'Confirmar facturación masiva';
    const confirmContent = esGlobal ? (
      <div>
        <p>Se generará <strong>1 factura global</strong> al público en general con {cuantas}.</p>
        <p>Periodo: <strong>{periodicidadLabel}</strong>, del {fechaInicioStr} al {fechaFinStr}.</p>
        {formaPagoFilter && <p>Solo ventas con forma de pago: <strong>{etiquetaFormaPago(formaPagoFilter)}</strong>.</p>}
        <p>Un concepto por ticket con su folio, IVA desglosado. Las cortesías completas ($0) no entran.</p>
      </div>
    ) : (
      <div>
        <p>Se generarán <strong>{selectedOrderIds.length}</strong> facturas (una por cada orden).</p>
        <p>Esta acción no se puede deshacer.</p>
      </div>
    );

    modal.confirm({
      title: confirmTitle,
      content: confirmContent,
      okText: esGlobal ? 'Generar Factura Global' : 'Facturar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const result = await facturarMasivo({
            orderIds: usarTodas ? [] : selectedOrderIds,
            // En la global el receptor es siempre el público en general; el backend lo resuelve.
            facturadorClienteId: esGlobal ? 0 : selectedClienteId!,
            metodoPago: 'PUE',
            modo: modoFacturacion,
            globalOptions: esGlobal
              ? {
                  periodicidad,
                  fechaInicio: fechaInicioStr!,
                  fechaFin: fechaFinStr!,
                  todasLasDelPeriodo: usarTodas,
                  formaPago: formaPagoFilter,
                }
              : undefined,
          }).unwrap();

          if (result.facturasGeneradas > 0) {
            const successMsg = esGlobal
              ? `Factura global generada (${result.facturas.length} órdenes incluidas). Revísala y timbrala en el paso 2.`
              : `${result.facturasGeneradas} factura(s) generada(s) exitosamente`;
            message.success(successMsg);
          }

          if (result.errores > 0) {
            mostrarErrores(`${result.errores} orden(es) no entraron`, result.detalleErrores);
          }

          setSelectedOrderIds([]);
          refetchOrdenes();
          refetchFacturas();
        } catch (error) {
          message.error(getErrorMessage(error));
        }
      },
    });
  };

  // Handle bulk stamping
  const handleTimbrarMasivo = async () => {
    if (selectedFacturaIds.length === 0) {
      message.warning('Selecciona al menos una factura para timbrar');
      return;
    }

    modal.confirm({
      title: 'Confirmar timbrado masivo',
      content: (
        <div>
          <p>Se timbrarán <strong>{selectedFacturaIds.length}</strong> factura(s).</p>
          <p>Esta acción consumirá timbres y no se puede deshacer.</p>
        </div>
      ),
      okText: 'Timbrar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const result = await timbrarMasivo({
            facturaIds: selectedFacturaIds,
          }).unwrap();

          if (result.timbradas > 0) {
            message.success(`${result.timbradas} factura(s) timbrada(s) exitosamente`);
          }

          if (result.errores > 0) {
            modal.warning({
              title: `${result.errores} factura(s) con error`,
              content: (
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {result.resultados
                    .filter((r: TimbradoResumen) => !r.success)
                    .map((r: TimbradoResumen, idx: number) => (
                      <Alert
                        key={idx}
                        type="error"
                        message={`Factura ${r.facturaId}`}
                        description={r.error}
                        style={{ marginBottom: 8 }}
                      />
                    ))}
                </div>
              ),
            });
          }

          setSelectedFacturaIds([]);
          refetchFacturas();
        } catch (error) {
          message.error(getErrorMessage(error));
        }
      },
    });
  };

  // Handle bulk regeneration
  const handleRegenerarMasivo = async () => {
    if (selectedFacturaIds.length === 0) {
      message.warning('Selecciona al menos una factura para regenerar');
      return;
    }

    modal.confirm({
      title: 'Confirmar regeneración masiva',
      content: (
        <div>
          <p>Se regenerarán <strong>{selectedFacturaIds.length}</strong> factura(s) en borrador.</p>
          <p>Se usará el mismo cliente fiscal que ya tienen asignado.</p>
        </div>
      ),
      okText: 'Regenerar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const result = await regenerarMasivo({
            facturaIds: selectedFacturaIds,
          }).unwrap();

          if (result.regeneradas > 0) {
            message.success(`${result.regeneradas} factura(s) regenerada(s) exitosamente`);
          }

          if (result.errores > 0) {
            modal.warning({
              title: `${result.errores} factura(s) con error`,
              content: (
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {result.resultados
                    .filter((r: RegeneracionResumen) => !r.success)
                    .map((r: RegeneracionResumen, idx: number) => (
                      <Alert
                        key={idx}
                        type="error"
                        message={`Factura ${r.facturaId}`}
                        description={r.error}
                        style={{ marginBottom: 8 }}
                      />
                    ))}
                </div>
              ),
            });
          }

          setSelectedFacturaIds([]);
          refetchFacturas();
        } catch (error) {
          message.error(getErrorMessage(error));
        }
      },
    });
  };

  // Calculate totals
  const totalOrdenesSeleccionadas = useMemo(() => {
    if (!ordenes) return 0;
    const incluidas = usarTodas ? ordenes : ordenes.filter(o => selectedOrderIds.includes(o.orderId));
    return incluidas.reduce((sum, o) => sum + o.total, 0);
  }, [ordenes, selectedOrderIds, usarTodas]);

  const cuantasOrdenes = usarTodas ? (ordenes?.length ?? 0) : selectedOrderIds.length;

  const totalFacturasSeleccionadas = useMemo(() => {
    if (!facturasData?.items) return 0;
    return facturasData.items
      .filter(f => selectedFacturaIds.includes(f.facturaId))
      .reduce((sum, f) => sum + f.total, 0);
  }, [facturasData, selectedFacturaIds]);

  const renderTotal = (record: OrdenSinFacturar) => (
    <Tooltip title={record.descuento > 0 ? `Bruto ${formatCurrency(record.totalBruto)} · descuento ${formatCurrency(record.descuento)}` : undefined}>
      <span className="fac-money">{formatCurrency(record.total)}</span>
    </Tooltip>
  );

  // Columns for orders table
  const ordenesColumns: ColumnsType<OrdenSinFacturar> = [
    {
      title: 'Orden',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 80,
      render: (orderNumber: string) => <span style={{ fontWeight: 600 }}>{orderNumber}</span>,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 130,
      render: (fecha: string) => (
        <span style={{ color: 'var(--text-secondary)' }}>
          {formatInTimeZone(fecha, 'DD/MM/YYYY HH:mm')}
        </span>
      ),
    },
    {
      title: 'Mesa',
      dataIndex: 'tableName',
      key: 'tableName',
      width: 100,
      render: (text: string) => text || '—',
    },
    {
      title: 'Forma de pago',
      dataIndex: 'paymentMethodName',
      key: 'paymentMethodName',
      width: 120,
    },
    {
      title: 'Ref. terminal',
      dataIndex: 'referenciaTerminal',
      key: 'referenciaTerminal',
      width: 130,
      render: (ref?: string) => ref || <span style={{ color: 'var(--text-secondary)' }}>—</span>,
    },
    {
      title: 'Total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (_: unknown, record: OrdenSinFacturar) => renderTotal(record),
    },
  ];

  // Para cuadrar contra el reporte de la terminal: folio del ticket, fecha, monto y referencia.
  const exportarOrdenes = () => {
    if (!ordenes || ordenes.length === 0) return;
    exportTablaCSV(
      ['Ticket', 'Fecha', 'Total', 'Forma de pago', 'Ref. terminal'],
      ordenes.map((o) => [
        o.orderNumber,
        formatInTimeZone(o.fecha, 'DD/MM/YYYY HH:mm'),
        o.total.toFixed(2),
        o.paymentMethodName ?? '',
        o.referenciaTerminal ?? '',
      ]),
      'ordenes-sin-facturar',
    );
  };

  // Columns for facturas table (solo borradores: el estado es implícito)
  const facturasColumns: ColumnsType<FacturaListItem> = [
    {
      title: 'Folio',
      key: 'folio',
      width: 110,
      render: (_: unknown, record: FacturaListItem) => (
        <span style={{ fontWeight: 600 }}>{record.serie}-{record.folio}</span>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 110,
      render: (fecha: string) => (
        <span style={{ color: 'var(--text-secondary)' }}>{formatInTimeZone(fecha, 'DD/MM/YYYY')}</span>
      ),
    },
    {
      title: 'Cliente',
      key: 'clienteRazonSocial',
      ellipsis: true,
      render: (_: unknown, record: FacturaListItem) => (
        <span>
          {record.esGlobal && <Tag color="blue" style={{ marginRight: 6 }}>Global · {record.ordenesCubiertas ?? 0}</Tag>}
          {record.clienteRazonSocial}
        </span>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (total: number) => <span className="fac-money">{formatCurrency(total)}</span>,
    },
  ];

  // Filter facturas to only show Borrador
  const facturasBorrador = useMemo(() => {
    return facturasData?.items.filter(f => f.estado === 'Borrador') || [];
  }, [facturasData]);

  // Una global no se regenera: "Regenerar" facturaba solo una de sus órdenes y borraba el resto.
  const seleccionIncluyeGlobal = facturasBorrador.some(
    f => selectedFacturaIds.includes(f.facturaId) && (f.esGlobal || (f.ordenesCubiertas ?? 1) > 1));

  // Mobile-optimized columns for orders
  const getMobileOrdenesColumns = (): ColumnsType<OrdenSinFacturar> => [
    {
      title: 'Orden',
      key: 'orden',
      render: (_, record) => (
        <div style={{ padding: '4px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 600 }}>#{record.orderNumber}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {formatInTimeZone(record.fecha, 'DD/MM HH:mm')}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {renderTotal(record)}
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {record.paymentMethodName}{record.referenciaTerminal ? ` · ${record.referenciaTerminal}` : ''}
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  // Mobile-optimized columns for facturas
  const getMobileFacturasColumns = (): ColumnsType<FacturaListItem> => [
    {
      title: 'Factura',
      key: 'factura',
      render: (_, record) => (
        <div style={{ padding: '4px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{record.serie}-{record.folio}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {formatInTimeZone(record.fecha, 'DD/MM/YY')}
              </div>
            </div>
            <div className="fac-money">{formatCurrency(record.total)}</div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {record.esGlobal ? `Global · ${record.ordenesCubiertas ?? 0} órdenes` : record.clienteRazonSocial}
          </div>
        </div>
      ),
    },
  ];

  const timbresRestantes = stampBalance
    ? stampBalance.totalDisponibles - selectedFacturaIds.length
    : null;

  const puedeGenerar = esGlobal
    ? periodoCompleto && (usarTodas ? (ordenes?.length ?? 0) > 0 : selectedOrderIds.length > 0)
    : selectedOrderIds.length > 0 && !!selectedClienteId;

  return (
    <div>
      {/* Paso 1: seleccionar órdenes */}
      <div className="fac-step">
        <div className="fac-step__head">
          <span className="fac-step__num">1</span>
          <span className="fac-step__title">
            {esGlobal ? 'Periodo de la factura global' : 'Selecciona órdenes sin facturar'}
          </span>
          <span className="fac-step__meta">{ordenes?.length ?? 0} órdenes</span>
          <div style={{ flex: 1 }} />
          <MobileDateRangePicker
            value={dateRange}
            onChange={(dates) => handleDateRangeChange(dates)}
            placeholder={['Inicio', 'Fin']}
          />
          <Select
            style={{ width: isMobile ? '100%' : 170 }}
            placeholder="Forma de pago"
            allowClear
            value={formaPagoFilter}
            onChange={setFormaPagoFilter}
          >
            {FORMAS_PAGO.map((f) => (
              <Option key={f.value} value={f.value}>{f.label}</Option>
            ))}
          </Select>
          {esGlobal ? (
            // La global va siempre al público en general; ya no se escoge cliente.
            <Text type="secondary" style={{ fontSize: 12 }}>Receptor: PÚBLICO EN GENERAL (XAXX010101000)</Text>
          ) : (
            <Select
              style={{ width: isMobile ? '100%' : 200 }}
              placeholder="Cliente fiscal"
              loading={loadingClientes}
              allowClear
              value={selectedClienteId}
              onChange={setSelectedClienteId}
              showSearch
              optionFilterProp="children"
            >
              {clientes?.map(cliente => (
                <Option key={cliente.id} value={cliente.facturadorClienteId}>
                  {cliente.rfc === 'XAXX010101000'
                    ? 'PÚBLICO GENERAL'
                    : `${cliente.razonSocial} (${cliente.rfc})`}
                </Option>
              ))}
            </Select>
          )}
          <Tooltip title="Exportar (CSV) para cuadrar contra la terminal">
            <Button type="text" icon={<DownloadOutlined />} onClick={exportarOrdenes} disabled={!ordenes?.length} />
          </Tooltip>
          <Tooltip title="Actualizar">
            <Button type="text" icon={<ReloadOutlined />} onClick={() => refetchOrdenes()} loading={loadingOrdenes} />
          </Tooltip>
        </div>

        {esGlobal && formaPagoFilter && (
          <div style={{ padding: '0 14px 8px' }}>
            <Alert
              type="info"
              showIcon
              message={`Global solo de ${etiquetaFormaPago(formaPagoFilter).toLowerCase()}`}
              description="La factura global debe amparar todas las ventas al público en general del periodo. Las que quedan fuera (otras formas de pago) necesitan su propia global."
            />
          </div>
        )}

        {ordenesResult?.truncado && (
          <div style={{ padding: '0 14px 8px' }}>
            <Alert
              type="warning"
              showIcon
              message={`Se muestran las ${ordenesResult.limite} órdenes más recientes del rango`}
              description={esGlobal
                ? 'La factura global con "todas las del periodo" sí incluye las que no se ven aquí.'
                : 'Acota el rango de fechas para ver el resto.'}
            />
          </div>
        )}
        {(ordenesResult?.excluidasPorTotalCero ?? 0) > 0 && (
          <div style={{ padding: '0 14px 8px' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {ordenesResult!.excluidasPorTotalCero} cortesía(s) con total $0 no se facturan y no aparecen en la lista.
            </Text>
          </div>
        )}

        <div className="fac-step__body">
          <Table
            rowKey="orderId"
            columns={isMobile ? getMobileOrdenesColumns() : ordenesColumns}
            dataSource={ordenes || []}
            loading={loadingOrdenes}
            size="small"
            scroll={isMobile ? { y: 240 } : { x: 600, y: 300 }}
            pagination={false}
            rowSelection={usarTodas ? undefined : {
              selectedRowKeys: selectedOrderIds,
              onChange: handleOrderSelectionChange,
            }}
          />
        </div>

        {/* Opciones de factura global, inline al elegir Global */}
        {esGlobal && (
          <div className="fac-global-opts">
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Periodicidad (SAT)</Text>
              <Select
                style={{ width: 210 }}
                size="small"
                value={periodicidad}
                onChange={(value) => setPeriodicidad(value)}
              >
                {PERIODICIDADES.filter((p) => p.value !== '05' || esRif).map((p) => (
                  <Option key={p.value} value={p.value}>{p.value} · {p.label}</Option>
                ))}
              </Select>
            </div>
            <Checkbox
              checked={todasLasDelPeriodo}
              onChange={(e) => setTodasLasDelPeriodo(e.target.checked)}
            >
              Todas las órdenes sin facturar del periodo
            </Checkbox>
            <Text type="secondary" style={{ fontSize: 12, flexBasis: '100%' }}>
              Un concepto por ticket (folio, IVA desglosado). El periodo son días locales del restaurante; el SAT pide
              emitir la global a más tardar 24 horas después de cerrar el periodo.
            </Text>
          </div>
        )}

        {/* Barra de acción fija: la selección no brinca el layout */}
        <div className="fac-actbar">
          <span className="fac-actbar__kv">
            <b>{cuantasOrdenes}</b> {cuantasOrdenes === 1 ? 'orden' : 'órdenes'} · <b>{formatCurrency(totalOrdenesSeleccionadas)}</b>
          </span>
          <Segmented
            size="small"
            value={modoFacturacion}
            onChange={(value) => setModoFacturacion(value as ModoFacturacionMasiva)}
            options={[
              { label: 'Individual', value: 'individual' },
              { label: 'Global', value: 'global' },
            ]}
          />
          {!esGlobal && selectedOrderIds.length > 0 && !selectedClienteId && (
            <span className="fac-actbar__hint">elige un cliente fiscal para continuar</span>
          )}
          {esGlobal && !periodoCompleto && (
            <span className="fac-actbar__hint">elige el periodo de la global</span>
          )}
          <div style={{ flex: 1 }} />
          <Button
            type="primary"
            onClick={handleFacturarMasivo}
            loading={facturando}
            disabled={!puedeGenerar}
            block={isMobile}
          >
            {esGlobal
              ? `Generar factura global (${cuantasOrdenes} ${cuantasOrdenes === 1 ? 'orden' : 'órdenes'})`
              : `Generar ${selectedOrderIds.length || ''} factura${selectedOrderIds.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>

      {/* Paso 2: timbrar borradores */}
      <div className="fac-step">
        <div className="fac-step__head">
          <span className="fac-step__num">2</span>
          <span className="fac-step__title">Timbra los borradores</span>
          <span className="fac-step__meta">
            {facturasBorrador.length > 0 ? `${facturasBorrador.length} pendientes` : 'sin pendientes'}
          </span>
          <div style={{ flex: 1 }} />
          <Tooltip title="Actualizar">
            <Button type="text" icon={<ReloadOutlined />} onClick={() => refetchFacturas()} loading={loadingFacturas} />
          </Tooltip>
        </div>

        {facturasBorrador.length === 0 ? (
          <div style={{ padding: 14 }}>
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="Sin facturas pendientes"
              description="No hay facturas en borrador pendientes de timbrar."
            />
          </div>
        ) : (
          <>
            <div className="fac-step__body">
              <Table
                rowKey="facturaId"
                columns={isMobile ? getMobileFacturasColumns() : facturasColumns}
                dataSource={facturasBorrador}
                loading={loadingFacturas}
                size="small"
                scroll={isMobile ? { y: 240 } : { x: 600, y: 300 }}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selectedFacturaIds,
                  onChange: handleFacturaSelectionChange,
                }}
              />
            </div>

            <div className="fac-actbar">
              <span className="fac-actbar__kv">
                <b>{selectedFacturaIds.length}</b> {selectedFacturaIds.length === 1 ? 'factura' : 'facturas'} · <b>{formatCurrency(totalFacturasSeleccionadas)}</b>
              </span>
              {selectedFacturaIds.length > 0 && timbresRestantes !== null && (
                <span className="fac-actbar__hint">
                  consumirá {selectedFacturaIds.length} {selectedFacturaIds.length === 1 ? 'timbre' : 'timbres'} · te {timbresRestantes === 1 ? 'quedaría' : 'quedarían'} {Math.max(timbresRestantes, 0)}
                </span>
              )}
              <div style={{ flex: 1 }} />
              <Tooltip title={seleccionIncluyeGlobal ? 'Una factura global no se regenera; bórrala y vuelve a generarla' : undefined}>
                <Button
                  icon={<SyncOutlined />}
                  onClick={handleRegenerarMasivo}
                  loading={regenerando}
                  disabled={selectedFacturaIds.length === 0 || seleccionIncluyeGlobal}
                  block={isMobile}
                >
                  Regenerar
                </Button>
              </Tooltip>
              <Button
                type="primary"
                icon={<SafetyCertificateOutlined />}
                onClick={handleTimbrarMasivo}
                loading={timbrando}
                disabled={selectedFacturaIds.length === 0}
                block={isMobile}
              >
                Timbrar {selectedFacturaIds.length > 0 ? selectedFacturaIds.length : ''}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FacturacionMasivaTab;
